/**
 * @file        src/pages/offer/AddOffer.jsx
 * @description Create / edit an offer: title, slug, coupon code, image upload,
 *              rich-text description, status, featured flag and service scope.
 *              Reused for /offers/new and /offers/:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-04
 * @changelog
 *   - 2026-07-04 Claude — Initial (Offer Management).
 *   - 2026-09-15 Claude — Coupon Code is a PICKER of live coupons, not a free-text
 *                          box. It accepted any 60-character string with no check
 *                          that such a coupon existed, and the storefront resolves
 *                          the code live — so a typo, a lapsed coupon, or a code
 *                          nobody had created yet saved with a success toast and
 *                          then produced an offer that simply never appeared.
 *                          The list carries BOTH audiences: a coupon's
 *                          `applicable_role` is an exact match at redemption
 *                          (CLAUDE.md §9a), and since an offer has no audience
 *                          column of its own, the coupon's role IS the offer's
 *                          audience. Each option says which, and the panel under
 *                          the field spells out the consequence, because
 *                          "Corporate (B2B)" means retail customers will not see
 *                          this offer at all. The server re-checks on save
 *                          (offers.service.assertCouponUsable) — this picker is
 *                          the convenience, not the boundary.
 *   - 2026-09-15 Claude — Added the one-line note that an offer is only the
 *                          marketing card: the discount comes from the matching
 *                          row in the Coupon List, and an offer whose code has no
 *                          live in-scope coupon is withheld from the storefront
 *                          entirely. Nothing on this screen said so, so a dead
 *                          offer looked like a bug rather than a missing coupon.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import { OFFER_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import ImageUpload from '../../components/ImageUpload';
import RichTextEditor from '../../components/RichTextEditor';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';

// A coupon whose audience is 'b2b' makes this offer corporate-only, and vice
// versa — the storefront withholds the whole card from the other audience.
const AUD_LABEL = { b2c: 'Retail (B2C)', b2b: 'Corporate (B2B)' };

const SERVICE_OPTIONS = ['Flight', 'Hotel', 'Oneway', 'Return', 'Multicity'].map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }];
const YESNO_OPTIONS = [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];

const schema = z.object({
  title: z.string().trim().min(1, 'Required').max(220),
  slug: z.string().trim().max(240).optional(),
  coupon_code: z.string().trim().max(60).optional(),
  service_for: z.enum(['Flight', 'Hotel', 'Oneway', 'Return', 'Multicity']),
});

const empty = {
  title: '', slug: '', coupon_code: '', image: '', description: '',
  service_for: 'Flight', is_active: true, is_featured: false,
};

export default function AddOffer() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: qk.offers.one(id),
    queryFn: () => get(`/offers/${id}`),
    enabled: isEdit,
  });

  // Live coupons for the chosen service. Refetches when service_for changes,
  // because a hotel offer and a flight offer can redeem different coupons.
  const { data: couponRaw, isLoading: couponsLoading } = useQuery({
    queryKey: qk.offers.couponOptions(form.service_for),
    queryFn: () => get(`/offers/coupon-options?serviceFor=${encodeURIComponent(form.service_for)}`),
    staleTime: 30_000,
  });
  const liveCoupons = couponRaw?.data || [];

  useEffect(() => {
    if (existing?.data) {
      const o = existing.data;
      setForm({
        title: o.title || '', slug: o.slug || '', coupon_code: o.coupon_code || '',
        image: o.image || '', description: o.description || '',
        service_for: o.service_for || 'Flight',
        is_active: !!o.is_active, is_featured: !!o.is_featured,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (b) => (isEdit ? patch(`/offers/${id}`, b) : post('/offers', b)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Offer updated' : 'Offer added', `${form.title} saved.`);
      qc.invalidateQueries({ queryKey: ['offers'] });
      navigate('/offers');
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe = {};
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
      return setErrors(fe);
    }
    setErrors({});
    const body = {
      title: parsed.data.title,
      service_for: parsed.data.service_for,
      is_active: form.is_active,
      is_featured: form.is_featured,
    };
    if (parsed.data.slug) body.slug = parsed.data.slug;
    // Sent even when blank: omitting it made "clear the coupon" impossible,
    // since PATCH skips undefined keys and the old code stayed put.
    body.coupon_code = parsed.data.coupon_code || '';
    if (form.image) body.image = form.image;
    if (form.description) body.description = form.description;
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selectedCoupon = liveCoupons.find((c) => c.code === form.coupon_code) || null;
  // A stored code that is no longer live must still be VISIBLE — silently
  // dropping it on open would clear the offer's coupon the next time anyone
  // saved an unrelated field. It is shown, flagged, and the server will refuse
  // the save until it is fixed or cleared.
  const staleCode = form.coupon_code && !selectedCoupon ? form.coupon_code : '';
  const couponOptions = [
    { value: '', label: '— No coupon (marketing card only) —' },
    ...liveCoupons.map((c) => ({
      value: c.code,
      label: `${c.code} · ${c.discount_label} · ${AUD_LABEL[c.audience] || c.audience} · ${String(c.brand || '').toUpperCase()}`,
    })),
    ...(staleCode ? [{ value: staleCode, label: `${staleCode} — not a live coupon` }] : []),
  ];

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Offer' : 'Add Offer'}
        subtitle="Offer Management"
        actions={<button className="btn ghost" onClick={() => navigate('/offers')}><Icon name="grid" /> Offer List</button>}
      />

      <div className="notice-banner info">{OFFER_NOTE}</div>

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Title" required err={errors.title}>
            <input value={form.title} onChange={on('title')} placeholder="Enter Title" />
          </Field>
          <Field label="Slug" err={errors.slug} hint="Auto from title if blank.">
            <input value={form.slug} onChange={on('slug')} placeholder="Enter Slug like offer-package" />
          </Field>
          <Field
            label="Coupon Code"
            err={errors.coupon_code}
            hint={couponsLoading ? 'Loading live coupons…'
              : liveCoupons.length ? 'Only coupons that are live right now for this service.'
                : 'No live coupon exists for this service — create one under Coupon List.'}
          >
            <Select
              value={form.coupon_code}
              onChange={(v) => setForm({ ...form, coupon_code: v })}
              options={couponOptions}
              placeholder="— No coupon (marketing card only) —"
              invalid={Boolean(errors.coupon_code || staleCode)}
            />
          </Field>

          {/* The audience is not a field on this form — it is whatever the
              chosen coupon says. Spelling it out is the point: picking a
              Corporate coupon hides this offer from every retail customer, and
              nothing else on the screen would have told the admin that. */}
          {selectedCoupon ? (
            <div className="field full">
              <div className="notice-banner info">
                <span>
                  <strong>{selectedCoupon.discount_label}</strong> · shown to{' '}
                  <strong>{AUD_LABEL[selectedCoupon.audience] || selectedCoupon.audience}</strong>{' '}
                  customers on the <strong>{String(selectedCoupon.brand || '').toUpperCase()}</strong> storefront
                  {selectedCoupon.valid_to ? <> · valid to {String(selectedCoupon.valid_to).slice(0, 10)}</> : null}.
                  {' '}The other audience will not see this offer at all.
                </span>
              </div>
            </div>
          ) : null}
          {staleCode ? (
            <div className="field full">
              <div className="notice-banner">
                <span>
                  <strong>{staleCode}</strong> is not a live coupon for this service (expired,
                  disabled, used up, or never created). This offer is hidden on the website
                  until you pick a live coupon or clear the field.
                </span>
              </div>
            </div>
          ) : null}

          <Field label="For Service" required err={errors.service_for}>
            <Select
              value={form.service_for}
              onChange={(v) => setForm({ ...form, service_for: v })}
              options={SERVICE_OPTIONS}
            />
          </Field>
          <Field label="Status">
            <Select
              value={String(form.is_active)}
              onChange={(v) => setForm({ ...form, is_active: v === 'true' })}
              options={STATUS_OPTIONS}
            />
          </Field>
          <Field label="Is Featured">
            <Select
              value={String(form.is_featured)}
              onChange={(v) => setForm({ ...form, is_featured: v === 'true' })}
              options={YESNO_OPTIONS}
            />
          </Field>

          <Field label="Image" full>
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
          </Field>

          <Field label="Description" full>
            <RichTextEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} />
          </Field>

          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Submit'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
